import { NavigateFunction } from "react-router-dom";
import { create } from "zustand";
import { useConsultsStore } from "./useConsultsStore";
import { useEmrStore } from "./useEmrStore";
import { useExpertsStore } from "./useExpertsStore";
import { useFilesStore } from "./useFilesStore";
import { usePlansStore } from "./usePlansStore";
import { useProductsStore } from "./useProductsStore";
import { useSessionStore } from "./useSessionStore";
import { useSurveyDataStore } from "./useSurveyDataStore";
import { useTrackersStore } from "./useTrackersStore";
import { useTutorialStore } from "./useTutorialStore";
import { useUserStore } from "./useUserStore";

interface AuthState {
    idToken: string | null;
    userId: string | null;
    accessToken: string | null;
    refreshToken: string | null;
    expiryTime: string | null;
}

interface AuthStore {
    auth: AuthState;
    login: (idToken: string, userId: string, accessToken: string, refreshToken: string, expiryIn: number) => void;
    logout: (navigate: NavigateFunction) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
    auth: (() => {
        const storedAuth = JSON.parse(localStorage.getItem('auth') || '{}');
        if (!storedAuth.idToken) {
            useSessionStore.getState().setSessionExpired(true);
        }
        return storedAuth;
    })(),

    login: (idToken, userId, accessToken, refreshToken, expiryIn) => {
        const expiryTime = new Date(Date.now() + expiryIn * 1000).toLocaleString();
        const authData: AuthState = { idToken, userId, accessToken, refreshToken, expiryTime };
        
        set({ auth: authData });
        useSessionStore.getState().setSessionExpired(false);

        localStorage.setItem('auth', JSON.stringify(authData));
    },

    logout: (navigate) => {
        set({ auth: { idToken: null, userId: null, accessToken: null, refreshToken: null, expiryTime: null } });
        useSessionStore.getState().setSessionExpired(false);

        localStorage.removeItem('auth');

        console.clear();
        
        const { clearUserData } = useUserStore.getState()
        const { clearExperts } = useExpertsStore.getState();
        const { clearSurveyData } = useSurveyDataStore.getState();
        const { clearConsultsData } = useConsultsStore.getState();
        const { clearPlans } = usePlansStore.getState();
        const { clearTutorial } = useTutorialStore.getState();
        const { clearProducts } = useProductsStore.getState();
        const { clearTrackers } = useTrackersStore.getState();
        const { clearEmrs } = useEmrStore.getState();
        const { clearFiles } = useFilesStore.getState();

        clearUserData()
        clearExperts();
        clearSurveyData();
        clearConsultsData();
        clearPlans();
        clearTutorial();
        clearProducts();
        clearTrackers();
        clearEmrs();
        clearFiles();

        navigate("/");
    },
}));

// You can also add selectors to access the auth data if needed
export const useAuth = () => {
    const authStore = useAuthStore();
    return authStore.auth;
};